import { useState } from 'react';
import { Sparkles, X, Loader2, ChevronRight, ExternalLink, CheckCircle2 } from 'lucide-react';
import sprayService, {
  type ChemicalDiscoverCandidate,
  type ChemicalLookupProposal,
} from '../../services/spray';
import Button from '../ui/Button';

interface Props {
  onClose: () => void;
  onCreated: (chemicalId: number) => void;
}

type Phase = 'name' | 'searching' | 'candidates' | 'identifying' | 'review' | 'creating' | 'done' | 'error';

const IDENTITY_FIELDS: { key: keyof ChemicalLookupProposal['chemical']; label: string; isUrl?: boolean }[] = [
  { key: 'product_name', label: 'Product name' },
  { key: 'trade_name', label: 'Trade name' },
  { key: 'manufacturer', label: 'Manufacturer' },
  { key: 'apvma_approval_number', label: 'APVMA #' },
  { key: 'formulation_type', label: 'Formulation' },
  { key: 'signal_word', label: 'Signal word' },
  { key: 'mode_of_action_group', label: 'MoA group' },
  { key: 'mode_of_action_type', label: 'MoA type' },
  { key: 'label_url', label: 'Label URL', isUrl: true },
  { key: 'sds_url', label: 'SDS URL', isUrl: true },
];

function CreateChemicalFromLookupModal({ onClose, onCreated }: Props) {
  const [phase, setPhase] = useState<Phase>('name');
  const [name, setName] = useState('');
  const [candidates, setCandidates] = useState<ChemicalDiscoverCandidate[]>([]);
  const [note, setNote] = useState('');
  const [selected, setSelected] = useState<ChemicalDiscoverCandidate | null>(null);
  const [identity, setIdentity] = useState<ChemicalLookupProposal | null>(null);
  const [createdId, setCreatedId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  const trimmedName = name.trim();

  // Type a name / active ingredient -> matching APVMA-registered products.
  const runSearch = async () => {
    if (trimmedName.length < 2) { setError('Enter at least 2 characters.'); return; }
    setError(null);
    setPhase('searching');
    const res = await sprayService.searchApvma(trimmedName);
    if (!res.success || !res.data) {
      setError(res.error?.message || 'Search failed');
      setPhase('error');
      return;
    }
    setCandidates(res.data.candidates || []);
    setNote(res.data.notes_for_user || '');
    setPhase('candidates');
  };

  // Pick a product -> build its full record from the APVMA register.
  const pickCandidate = async (candidate: ChemicalDiscoverCandidate) => {
    setSelected(candidate);
    setError(null);
    setPhase('identifying');
    const res = await sprayService.lookupCandidateIdentity(candidate);
    if (!res.success || !res.data) {
      setError(res.error?.message || 'Could not build the product record');
      setPhase('error');
      return;
    }
    setIdentity(res.data.proposal);
    setPhase('review');
  };

  const doCreate = async (chemicalFields?: ChemicalLookupProposal['chemical']) => {
    setError(null);
    setPhase('creating');
    const productName =
      chemicalFields?.product_name?.trim() || selected?.product_name?.trim() || trimmedName;

    const createRes = await sprayService.createChemical({ product_name: productName });
    if (!createRes.success || !createRes.data) {
      setError(createRes.error?.message || 'Failed to create chemical record');
      setPhase('error');
      return;
    }
    const id = createRes.data.id;

    if (chemicalFields) {
      const applyRes = await sprayService.applyChemicalLookup(id, { chemical: chemicalFields });
      if (!applyRes.success) {
        setError(
          (applyRes.error?.message || 'Could not apply the looked-up fields') +
            ' — the blank record was created; edit it manually.',
        );
        setCreatedId(id);
        setPhase('error');
        return;
      }
    }
    setCreatedId(id);
    setPhase('done');
  };

  const headerTitle =
    phase === 'review' || phase === 'identifying'
      ? `Confirm — ${selected?.product_name ?? trimmedName}`
      : 'Add chemical from the APVMA register';

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full my-4">
        <div className="px-6 py-4 border-b border-neutral-200 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-purple-600" />
            <h3 className="text-base font-semibold text-neutral-800">{headerTitle}</h3>
          </div>
          <button onClick={onClose} className="text-neutral-400 hover:text-neutral-600" aria-label="Close">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="px-6 py-4 max-h-[70vh] overflow-y-auto">
          {phase === 'name' && (
            <div className="space-y-4">
              <p className="text-sm text-neutral-700">
                Type a chemical name or active ingredient (e.g. <strong>Dicamba</strong>,{' '}
                <strong>Confidor</strong>, <strong>Roundup</strong>). We'll show the matching products
                registered with the APVMA — pick the one you have.
              </p>
              <div>
                <label className="block text-xs font-medium text-neutral-600 mb-1">Chemical / active / product</label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') runSearch(); }}
                  placeholder="e.g. Dicamba, glyphosate, Confidor 200 SC"
                  className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                  autoFocus
                />
              </div>
              {error && (
                <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{error}</div>
              )}
            </div>
          )}

          {phase === 'searching' && <Spinner text="Searching the APVMA register…" />}

          {phase === 'candidates' && (
            <div className="space-y-4">
              {note && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-900">{note}</div>
              )}
              {candidates.length === 0 ? (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-900">
                  No APVMA-registered products found for "<strong>{trimmedName}</strong>". Try the active
                  ingredient or a shorter name, or create a blank record and fill it in manually.
                </div>
              ) : (
                <>
                  <p className="text-sm text-neutral-700">Pick the product you have:</p>
                  <div className="space-y-2">
                    {candidates.map((c, i) => (
                      <button
                        key={i}
                        onClick={() => pickCandidate(c)}
                        className="w-full text-left border border-neutral-200 rounded-lg px-3 py-2.5 hover:border-purple-400 hover:bg-purple-50 transition flex items-center justify-between gap-3"
                      >
                        <div className="min-w-0">
                          <div className="text-sm font-medium text-neutral-800 truncate">{c.product_name}</div>
                          <div className="text-xs text-neutral-500 mt-0.5 truncate">
                            {[
                              c.manufacturer,
                              c.active_constituents,
                              c.formulation_type,
                              c.apvma_approval_number ? `APVMA ${c.apvma_approval_number}` : null,
                            ]
                              .filter(Boolean)
                              .join(' · ') || '— no metadata —'}
                          </div>
                        </div>
                        <ChevronRight className="h-4 w-4 text-neutral-400 shrink-0" />
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}

          {phase === 'identifying' && (
            <Spinner text="Building the record from the APVMA register…" />
          )}

          {phase === 'review' && identity && (
            <div className="space-y-4">
              {identity.notes_for_user && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-900">
                  {identity.notes_for_user}
                </div>
              )}
              <div className="border border-neutral-200 rounded-lg">
                <table className="w-full text-sm">
                  <tbody>
                    {IDENTITY_FIELDS.map(({ key, label, isUrl }) => {
                      const v = identity.chemical[key];
                      if (v == null || v === '') return null;
                      return (
                        <tr key={key} className="border-b border-neutral-100 last:border-0">
                          <td className="px-3 py-2 text-xs font-medium text-neutral-500 w-36">{label}</td>
                          <td className="px-3 py-2 text-neutral-800 break-words">
                            {isUrl ? (
                              <a
                                href={String(v)}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-purple-600 hover:underline inline-flex items-center gap-1"
                              >
                                {String(v).length > 60 ? String(v).slice(0, 60) + '…' : String(v)}
                                <ExternalLink className="h-3 w-3" />
                              </a>
                            ) : (
                              String(v)
                            )}
                          </td>
                        </tr>
                      );
                    })}
                    {Array.isArray(identity.chemical.active_ingredients) &&
                      identity.chemical.active_ingredients.length > 0 && (
                        <tr className="border-b border-neutral-100 last:border-0">
                          <td className="px-3 py-2 text-xs font-medium text-neutral-500 w-36">Active ingredients</td>
                          <td className="px-3 py-2 text-neutral-800 break-words">
                            {identity.chemical.active_ingredients
                              .map((ai) => {
                                const conc = ai.concentration_gl
                                  ? `${ai.concentration_gl} g/L`
                                  : ai.concentration_pct
                                  ? `${ai.concentration_pct}%`
                                  : '';
                                return `${ai.name}${conc ? ` ${conc}` : ''}`;
                              })
                              .join(', ')}
                          </td>
                        </tr>
                      )}
                  </tbody>
                </table>
              </div>
              {identity.chemical.sources.length > 0 && (
                <div className="text-xs text-neutral-500">
                  Source:{' '}
                  {identity.chemical.sources.map((s, i) => (
                    <a
                      key={i}
                      href={s}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-purple-600 hover:underline mr-2 inline-flex items-center gap-1"
                    >
                      APVMA PUBCRIS<ExternalLink className="h-3 w-3" />
                    </a>
                  ))}
                </div>
              )}
              <p className="text-xs text-neutral-500">
                Creating the record saves these fields. Add the registered crops &amp; rates from the
                chemical's page next (“Import from APVMA”).
              </p>
            </div>
          )}

          {phase === 'creating' && <Spinner text="Creating chemical record…" />}

          {phase === 'done' && (
            <div className="space-y-3 py-4">
              <div className="flex items-center gap-2 text-emerald-700">
                <CheckCircle2 className="h-5 w-5" />
                <span className="text-sm font-medium">Chemical record created</span>
              </div>
              <p className="text-sm text-neutral-700">
                Identity saved. Open the record to review and import its registered crops.
              </p>
            </div>
          )}

          {phase === 'error' && (
            <div className="py-8">
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-sm text-red-900">
                <div className="font-semibold mb-1">Something went wrong</div>
                <div>{error}</div>
              </div>
            </div>
          )}
        </div>

        <div className="px-6 py-4 border-t border-neutral-200 flex items-center justify-end gap-2">
          {phase === 'name' && (
            <>
              <Button variant="outline" size="sm" onClick={onClose}>Cancel</Button>
              <Button variant="primary" size="sm" onClick={runSearch} disabled={trimmedName.length < 2}>
                Search
              </Button>
            </>
          )}
          {phase === 'candidates' && (
            <>
              <Button variant="outline" size="sm" onClick={() => setPhase('name')}>Back</Button>
              <Button variant="outline" size="sm" onClick={() => doCreate()}>
                {candidates.length === 0 ? 'Create blank record' : 'None match — create blank'}
              </Button>
            </>
          )}
          {phase === 'review' && (
            <>
              <Button variant="outline" size="sm" onClick={() => setPhase('candidates')}>Back</Button>
              <Button variant="primary" size="sm" onClick={() => identity && doCreate(identity.chemical)}>
                Create chemical record
              </Button>
            </>
          )}
          {phase === 'done' && createdId != null && (
            <Button variant="primary" size="sm" onClick={() => onCreated(createdId)}>
              Open chemical
            </Button>
          )}
          {phase === 'error' && (
            <>
              <Button variant="outline" size="sm" onClick={onClose}>Close</Button>
              {createdId != null ? (
                <Button variant="primary" size="sm" onClick={() => onCreated(createdId)}>Open record</Button>
              ) : (
                <Button variant="primary" size="sm" onClick={() => setPhase('name')}>Try again</Button>
              )}
            </>
          )}
          {(phase === 'searching' || phase === 'identifying' || phase === 'creating') && (
            <Button variant="outline" size="sm" disabled>Working…</Button>
          )}
        </div>
      </div>
    </div>
  );
}

function Spinner({ text, sub }: { text: string; sub?: string }) {
  return (
    <div className="py-12 text-center">
      <Loader2 className="h-8 w-8 text-purple-600 animate-spin mx-auto mb-3" />
      <p className="text-sm text-neutral-700">{text}</p>
      {sub && <p className="text-xs text-neutral-500 mt-1">{sub}</p>}
    </div>
  );
}

export default CreateChemicalFromLookupModal;
